/**
 * Where to draw a GA area nobody has drawn (D-184).
 *
 * A GA area carries an outline only if somebody opened the map editor and drew
 * one, and almost nobody has: a real room's lawn and its fast-pass queue are
 * both capacity with no shape. `SeatMapView` skipped those entirely, so a
 * general-admission area was invisible on the very picture a customer buys
 * from — and on a GA-only show the map drew nothing at all.
 *
 * So an area with no outline gets a plain labeled block, laid out below
 * everything else. It is **worked out at render time and never stored**, which
 * is the whole point: a guess at where a lawn sits costs nothing if it is
 * wrong, needs no draft manifest to write, and disappears the moment somebody
 * draws the real thing.
 *
 * Deterministic on purpose — the same room lays out the same way every time,
 * whatever order the areas arrive in — so a customer who comes back tomorrow
 * finds the lawn where they left it.
 *
 * Pure: no DOM, no Konva, no I/O.
 */
import { boundsOf } from './geometry';
import type { Bounds, Point } from './geometry';

/** Room left between the seats and the blocks, and between the blocks. */
const GAP = 40;
const HEIGHT = 110;
const MIN_WIDTH = 150;
const MAX_WIDTH = 420;

export interface AutoBlockInput {
  id: string;
  /** Sorted on, so the layout does not depend on the order they arrive in. */
  code: string;
  capacity: number;
}

export interface AutoBlock {
  ga_area_id: string;
  points: Point[];
}

export interface AutoBlockLayout {
  blocks: AutoBlock[];
  /** How tall the canvas has to be for the blocks to fit on it. */
  canvas_height: number;
}

/**
 * Lay out one block per area, left to right, wrapping at the canvas width.
 *
 * Width follows capacity, because a 900-place lawn beside a 40-place box should
 * not look like the same thing, but it is clamped at both ends: a tiny area
 * still needs its name to fit, and a huge one must not become the room.
 */
export function autoPlaceGaBlocks(areas: readonly AutoBlockInput[], canvasWidth: number, occupied: Bounds | null): AutoBlockLayout {
  const width = Math.max(canvasWidth, MIN_WIDTH + GAP * 2);
  if (areas.length === 0) return { blocks: [], canvas_height: occupied ? occupied.maxY : 0 };

  const ordered = [...areas].sort((a, b) => a.code.localeCompare(b.code) || a.id.localeCompare(b.id));
  const largest = Math.max(...ordered.map((a) => a.capacity), 1);

  const top = (occupied ? occupied.maxY : 0) + GAP;
  const blocks: AutoBlock[] = [];
  let x = GAP;
  let y = top;

  for (const area of ordered) {
    const share = Math.sqrt(Math.max(area.capacity, 1) / largest);
    const blockWidth = Math.round(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, MAX_WIDTH * share)));
    // Wrap before drawing, never after, so a block is never half off the edge.
    if (x > GAP && x + blockWidth > width - GAP) {
      x = GAP;
      y += HEIGHT + GAP;
    }
    blocks.push({
      ga_area_id: area.id,
      points: [
        [x, y],
        [x + blockWidth, y],
        [x + blockWidth, y + HEIGHT],
        [x, y + HEIGHT],
      ],
    });
    x += blockWidth + GAP;
  }

  return { blocks, canvas_height: y + HEIGHT + GAP };
}

/** The extent of everything already on the map, so the blocks go below it. */
export function occupiedBounds(points: readonly Point[]): Bounds | null {
  return boundsOf([...points]);
}
